import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";

import type { PottingSendFlow, ScsPottingDraftTableRow } from "./pottingSendApi";

function flowLabel(flow: PottingSendFlow): string {
  return flow === "first_pass" ? "First pass" : "Rework";
}

export function ScsPottingDraftTable({
  rows,
  isMutating,
  writesAllowed,
  hasInvalidFi3c,
  defaultExpanded = false,
  onRemoveFirstPass,
}: {
  rows: ScsPottingDraftTableRow[];
  isMutating: boolean;
  writesAllowed: boolean;
  hasInvalidFi3c: boolean;
  defaultExpanded?: boolean;
  onRemoveFirstPass: (id: number) => void;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const counts = useMemo(() => {
    let firstPass = 0;
    let rework = 0;
    for (const row of rows) {
      if (row.sendFlow === "first_pass") {
        firstPass += 1;
      } else {
        rework += 1;
      }
    }
    return { firstPass, rework };
  }, [rows]);

  return (
    <Card>
      <CardHeader className="gap-2">
        <button
          type="button"
          className="flex w-full items-start gap-2 text-left"
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? (
            <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" aria-hidden />
          ) : (
            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" aria-hidden />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-base">Potting draft</CardTitle>
              <Badge tone="muted">{rows.length} row{rows.length === 1 ? "" : "s"}</Badge>
              {hasInvalidFi3c ? <Badge tone="warning">FI-3C core feature issue</Badge> : null}
            </div>
            <p className="mt-1 text-sm text-slate-500">
              Open vendor send rows not yet on a purchase order — first pass from Pre-Encapsulation and rework from QC
              Incidents. The Flow column shows which path each unit follows.
            </p>
            {hasInvalidFi3c ? (
              <p className="mt-1 text-xs text-amber-800">
                One or more FI-3C rows are missing a valid N, B, or Q core feature — fix before send.
              </p>
            ) : null}
          </div>
        </button>
      </CardHeader>
      {expanded ? (
        <CardBody className="space-y-3">
          <div className="flex flex-wrap gap-2 text-xs text-slate-600">
            <Badge tone="muted">Total {rows.length}</Badge>
            <Badge tone="info">First pass {counts.firstPass}</Badge>
            <Badge tone="info">Rework {counts.rework}</Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Flow</th>
                  <th className="px-3 py-2 text-left">Id</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Serial</th>
                  <th className="px-3 py-2 text-left">Carton</th>
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-left">Core feature</th>
                  <th className="px-3 py-2 text-right">Remove</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={`${row.sendFlow}:${row.id}`}>
                    <td className="px-3 py-2">
                      <Badge tone={row.sendFlow === "first_pass" ? "info" : "warning"}>{flowLabel(row.sendFlow)}</Badge>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{row.id}</td>
                    <td className="px-3 py-2">
                      <Badge tone={row.rowKind === "FI-3C" ? "info" : row.rowKind === "FI-5A Chinese" ? "info" : "muted"}>
                        {row.rowKind}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{row.serial ?? "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{row.cartonNumber ?? "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {row.laborSku && row.productLabel ? (
                        <>
                          <span className="font-mono">{row.laborSku}</span>
                          <span className="text-slate-400"> · </span>
                          {row.productLabel}
                        </>
                      ) : row.finishedSku ? (
                        <span className="font-mono">{row.finishedSku}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">{row.skuCoreFeature ?? "—"}</td>
                    <td className="px-3 py-2 text-right">
                      {row.sendFlow === "first_pass" ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={!writesAllowed || isMutating}
                          onClick={() => onRemoveFirstPass(row.id)}
                        >
                          Remove
                        </Button>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">No devices are on the potting draft.</p>
          ) : null}
        </CardBody>
      ) : null}
    </Card>
  );
}
